(() => {
  const root = () => window.Shopify?.routes?.root || '/';
  async function request(url, options = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal, headers: { Accept: 'application/json', ...options.headers } });
      const data = await response.json();
      if (!response.ok || data.status || data.errors) throw new Error(data.description || data.message || (typeof data.errors === 'string' ? data.errors : ''));
      return data;
    } finally { clearTimeout(timeout); }
  }
  function updateCount(cart) {
    document.querySelectorAll('[data-lente-cart-count]').forEach(el => { el.textContent = '(' + cart.item_count + ')'; });
  }
  const toast = () => document.querySelector('[data-lente-cart-toast]');
  window.LenteCartUI = {
    updateCount,
    async added() {
      const notice = toast();
      if (notice) notice.hidden = false;
      try {
        const cart = await request((window.routes?.cart_url || root() + 'cart') + '.js');
        updateCount(cart);
        if (typeof publish === 'function') await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'lente-add-notice', cartData: cart });
      } catch (error) {
        const count = document.querySelector('[data-lente-cart-count]');
        if (count) count.textContent = '';
      }
    }
  };
  document.addEventListener('click', event => { if (event.target.closest('[data-toast-close]')) { const notice = toast(); if (notice) notice.hidden = true; } });
  document.addEventListener('keydown', event => { if (event.key === 'Escape') { const notice = toast(); if (notice) notice.hidden = true; } });
  if (customElements.get('lente-cart')) return;
  customElements.define('lente-cart', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.addEventListener('click', event => {
        const remove = event.target.closest('[data-remove]');
        const step = event.target.closest('[data-quantity-step]');
        if (!remove && !step) return;
        event.preventDefault();
        if (this.busy || this.checkingOut) return;
        const row = (remove || step).closest('[data-line-key]');
        const input = row.querySelector('input[name="updates[]"]');
        let quantity = 0;
        if (step) {
          const increment = Number(input.step) || 1;
          const minimum = Number(input.dataset.min) || 1;
          quantity = Number(input.value) + Number(step.dataset.quantityStep) * increment;
          if (quantity > 0 && quantity < minimum) quantity = Number(step.dataset.quantityStep) > 0 ? minimum : 0;
          if (input.max && quantity > Number(input.max)) return;
        }
        this.change(row.dataset.lineKey, Math.max(0, quantity), input);
      }, { signal });
      this.addEventListener('change', event => {
        if (!event.target.matches('input[name="updates[]"]')) return;
        const input = event.target;
        if (this.busy || this.checkingOut) { input.value = input.dataset.previous; return; }
        const value = Number(input.value), min = Number(input.dataset.min) || 1, step = Number(input.step) || 1;
        if (!Number.isInteger(value) || value < 0 || (value > 0 && (value < min || value % step !== 0)) || (input.max && value > Number(input.max))) {
          input.value = input.dataset.previous;
          this.error(this.dataset.error);
          return;
        }
        this.change(input.closest('[data-line-key]').dataset.lineKey, value, input);
      }, { signal });
      this.addEventListener('submit', event => {
        if (!event.target.matches('.lente-cart__form')) return;
        if (this.submitting) return;
        event.preventDefault();
        if (this.busy || this.checkingOut) return;
        this.checkout(event.target);
      }, { signal });
      window.addEventListener('pageshow', () => this.resetCheckout(), { signal });
      window.addEventListener('pagehide', () => { clearTimeout(this.checkoutRecovery); }, { signal });
      if (typeof subscribe === 'function') this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
        if (event.source === 'lente-minimal-cart' || this.busy || this.checkingOut) return;
        this.refresh().catch(() => this.error(this.dataset.error));
      });
    }
    disconnectedCallback() { this.abort?.abort(); this.abort = null; this.unsubscribe?.(); clearTimeout(this.checkoutLabelTimer); clearTimeout(this.checkoutTimer); clearTimeout(this.checkoutRecovery); }
    error(message) { this.querySelector('[data-cart-error]').textContent = message; }
    setBusy(busy) {
      this.busy = busy;
      this.setAttribute('aria-busy', String(busy));
      this.querySelectorAll('[data-checkout]').forEach(button => { button.disabled = busy; });
    }
    async refresh() {
      const url = new URL(this.dataset.cartUrl, window.location.origin);
      url.searchParams.set('sections', this.dataset.sectionId);
      const data = await request(url.href);
      this.render(data[this.dataset.sectionId]);
    }
    render(html) {
      if (!html) throw new Error(this.dataset.error);
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('lente-cart [data-cart-content]');
      if (!content) throw new Error(this.dataset.error);
      this.querySelector('[data-cart-content]').replaceChildren(...content.childNodes);
    }
    async change(key, quantity, input) {
      this.error('');
      const control = document.activeElement;
      const focusStep = control?.dataset.quantityStep;
      const wasRemove = control?.matches('[data-remove]');
      this.setBusy(true);
      let updated = false;
      try {
        const cart = await request(this.dataset.changeUrl + '.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity, sections: [this.dataset.sectionId], sections_url: window.location.pathname })
        });
        updated = true;
        updateCount(cart);
        const html = cart.sections?.[this.dataset.sectionId];
        if (html) this.render(html); else await this.refresh();
        this.querySelector('[data-cart-status]').textContent = '';
        if (typeof publish === 'function') await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'lente-minimal-cart', cartData: cart });
        const row = [...this.querySelectorAll('[data-line-key]')].find(el => el.dataset.lineKey === key);
        const target = row ? (focusStep ? [...row.querySelectorAll('[data-quantity-step]')].find(el => el.dataset.quantityStep === focusStep) : row.querySelector('input')) : this.querySelector(wasRemove ? '[data-remove]' : '[data-checkout]');
        target?.focus({ preventScroll: true });
      } catch (error) {
        if (updated) { try { await this.refresh(); } catch {} }
        else if (input?.isConnected) input.value = input.dataset.previous;
        this.error(error.message || this.dataset.error);
      } finally { this.setBusy(false); }
    }
    checkout(form) {
      if (!form.reportValidity()) return;
      this.checkingOut = true;
      this.error('');
      const button = form.querySelector('[data-checkout]');
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('aria-busy', 'true');
      button.classList.add('is-blooming');
      const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
      const showPending = () => {
        button.classList.add('is-pending');
        this.querySelector('[data-cart-status]').textContent = button.querySelector('.lente-checkout__pending').innerText;
      };
      const begin = () => {
        showPending();
        button.classList.add('is-continuing');
        this.checkoutRecovery = setTimeout(() => { this.resetCheckout(); this.error(this.dataset.checkoutError); }, 12000);
        try {
          this.submitting = true;
          form.requestSubmit(button);
          this.submitting = false;
        } catch (error) { this.resetCheckout(); this.error(this.dataset.checkoutError); }
      };
      if (reduced) begin();
      else {
        this.checkoutLabelTimer = setTimeout(showPending, 380);
        this.checkoutTimer = setTimeout(begin, 560);
      }
    }
    resetCheckout() {
      clearTimeout(this.checkoutLabelTimer); clearTimeout(this.checkoutTimer); clearTimeout(this.checkoutRecovery);
      this.checkingOut = false; this.submitting = false;
      const button = this.querySelector('[data-checkout]');
      if (button) { button.classList.remove('is-blooming', 'is-pending', 'is-continuing'); button.removeAttribute('aria-disabled'); button.removeAttribute('aria-busy'); }
      const status = this.querySelector('[data-cart-status]');
      if (status) status.textContent = '';
    }
  });
})();

